import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import {
  getHaccpNegativeRules,
  getHaccpPositiveRules,
  saveHaccpNegativeRules,
  saveHaccpPositiveRules,
} from './useHaccpConfigurations';

function emptyPhase() {
  return {
    name: '',
    required: true,
    min_temperature_c: '',
    max_temperature_c: '',
    tolerance_c: '',
    deadline_minutes_after_start: '',
    photo_allowed: false,
    photo_required: false,
  };
}

function blankRule(equipment) {
  return {
    equipment_id: equipment.id,
    equipment_name: equipment.label || `Abbattitore ${equipment.number}`,
    max_cycle_minutes: '',
    product_required: false,
    lot_required: false,
    photo_allowed: false,
    photo_required: false,
    corrective_action_required: true,
    nonconformity_procedure: '',
    corrective_action_instructions: '',
    phases: [emptyPhase()],
  };
}

function toEditorRule(rule, equipment) {
  return {
    ...blankRule(equipment),
    ...rule,
    equipment_name: equipment.label || `Abbattitore ${equipment.number}`,
    max_cycle_minutes: String(rule.max_cycle_minutes ?? ''),
    phases: (rule.phases || []).map((phase) => ({
      ...phase,
      min_temperature_c: String(phase.min_temperature_c ?? ''),
      max_temperature_c: String(phase.max_temperature_c ?? ''),
      tolerance_c: phase.tolerance_c == null ? '' : String(phase.tolerance_c),
      deadline_minutes_after_start: phase.deadline_minutes_after_start == null
        ? ''
        : String(phase.deadline_minutes_after_start),
    })),
  };
}

function validRule(rule) {
  return Number(rule.max_cycle_minutes) > 0
    && Boolean(rule.nonconformity_procedure.trim())
    && Boolean(rule.corrective_action_instructions.trim())
    && (!rule.photo_required || (rule.photo_allowed && rule.phases.every((phase) => phase.photo_allowed)))
    && (rule.photo_allowed || rule.phases.every((phase) => !phase.photo_required))
    && rule.phases.length > 0
    && rule.phases.some((phase) => phase.required)
    && rule.phases.every((phase) => phase.name.trim()
      && phase.min_temperature_c !== ''
      && phase.max_temperature_c !== ''
      && Number(phase.min_temperature_c) <= Number(phase.max_temperature_c)
      && (!phase.photo_required || phase.photo_allowed));
}

export default function PositiveBlastChillingRulesEditor({
  kind = 'positive',
  locationId,
  draft,
  modules,
  equipment,
  configDirty,
  onDirtyChange,
  onSaved,
}) {
  const negative = kind === 'negative';
  const moduleCode = negative ? 'BLAST_CHILLING_NEGATIVE' : 'BLAST_CHILLING_POSITIVE';
  const title = negative ? 'Abbattimento negativo' : 'Abbattimento positivo';
  const getRules = negative ? getHaccpNegativeRules : getHaccpPositiveRules;
  const saveRules = negative ? saveHaccpNegativeRules : saveHaccpPositiveRules;
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [dirty, setDirty] = useState(false);
  const eligible = useMemo(
    () => equipment.filter((item) => item.id && item.kind === 'BLAST_CHILLER'),
    [equipment],
  );
  const selected = modules.has(moduleCode);
  const readOnly = draft?.status !== 'DRAFT';

  useEffect(() => {
    let cancelled = false;
    if (!draft?.id || !selected) {
      setRules([]);
      setDirty(false);
      onDirtyChange(false);
      return undefined;
    }
    setLoading(true);
    setError('');
    getRules(locationId, draft.id)
      .then((response) => {
        if (cancelled) return;
        const byEquipment = new Map((response.rules || []).map((rule) => [rule.equipment_id, rule]));
        setRules(eligible.map((item) => (
          byEquipment.has(item.id)
            ? toEditorRule(byEquipment.get(item.id), item)
            : blankRule(item)
        )));
        setDirty(false);
        onDirtyChange(false);
      })
      .catch(() => {
        if (!cancelled) setError(`Procedura ${negative ? 'negativa' : 'positiva'} non disponibile. Ricarica la bozza.`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [draft?.id, draft?.revision, selected, eligible, locationId, onDirtyChange, getRules, negative]);

  const updateRule = (equipmentId, patch) => {
    setRules((items) => items.map((rule) => (
      rule.equipment_id === equipmentId ? { ...rule, ...patch } : rule
    )));
    setDirty(true);
    onDirtyChange(true);
    setMessage('');
  };

  const updatePhase = (equipmentId, index, patch) => {
    const rule = rules.find((item) => item.equipment_id === equipmentId);
    if (!rule) return;
    updateRule(equipmentId, {
      phases: rule.phases.map((phase, phaseIndex) => (
        phaseIndex === index ? { ...phase, ...patch } : phase
      )),
    });
  };

  const save = async () => {
    if (!draft || readOnly || saving || configDirty || !rules.length || !rules.every(validRule)) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = rules.map((rule) => ({
        equipment_id: rule.equipment_id,
        max_cycle_minutes: Number(rule.max_cycle_minutes),
        product_required: rule.product_required,
        lot_required: rule.lot_required,
        photo_allowed: rule.photo_allowed,
        photo_required: rule.photo_required,
        corrective_action_required: rule.corrective_action_required,
        nonconformity_procedure: rule.nonconformity_procedure.trim(),
        corrective_action_instructions: rule.corrective_action_instructions.trim(),
        phases: rule.phases.map((phase) => ({
          name: phase.name.trim(),
          required: phase.required,
          min_temperature_c: phase.min_temperature_c,
          max_temperature_c: phase.max_temperature_c,
          tolerance_c: phase.tolerance_c === '' ? null : phase.tolerance_c,
          deadline_minutes_after_start: phase.deadline_minutes_after_start === ''
            ? null
            : Number(phase.deadline_minutes_after_start),
          photo_allowed: phase.photo_allowed,
          photo_required: phase.photo_required,
        })),
      }));
      await saveRules(locationId, draft.id, draft.revision, payload);
      setDirty(false);
      onDirtyChange(false);
      await onSaved();
      setMessage('Procedura salvata nella bozza.');
    } catch (saveError) {
      setError(saveError?.status === 409
        ? 'La bozza è stata aggiornata. Ricaricala prima di salvare.'
        : saveError?.status === 422
          ? 'Completa i valori e le fasi richiesti dalla procedura.'
          : 'Non è stato possibile salvare la procedura. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="haccp-editor-section" data-testid={`haccp-${kind}-rules-editor`}>
      <div className="haccp-section-heading">
        <div>
          <span className="haccp-section-kicker">{negative ? '05' : '04'} · Procedura {negative ? 'negativa' : 'positiva'}</span>
          <h2>{title}</h2>
          <p>
            Inserisci le fasi, i limiti e i tempi previsti dal produttore e dal piano HACCP
            approvato. Non sono applicati valori universali.
          </p>
        </div>
      </div>
      {!selected ? (
        <p className="haccp-temperature-rule-note">Seleziona {title} per configurare le procedure.</p>
      ) : configDirty ? (
        <p className="haccp-temperature-rule-note" role="status">
          Salva prima attività e attrezzature; poi configura le procedure per gli abbattitori.
        </p>
      ) : loading ? (
        <p className="haccp-temperature-rule-note" role="status">Caricamento delle procedure…</p>
      ) : eligible.length === 0 ? (
        <p className="haccp-temperature-rule-note">
          Aggiungi e salva almeno un abbattitore prima di configurare il ciclo.
        </p>
      ) : (
        <>
          {rules.map((rule) => (
            <article className="haccp-positive-rule-card" key={rule.equipment_id}>
              <h3>{rule.equipment_name}</h3>
              <div className="haccp-positive-rule-grid">
                <label className="haccp-field">
                  <span>Durata massima del ciclo, minuti</span>
                  <input
                    type="number" min="1" max="10080" step="1"
                    value={rule.max_cycle_minutes} disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, { max_cycle_minutes: event.target.value })}
                  />
                </label>
                <label className="haccp-checkline">
                  <input type="checkbox" checked={rule.product_required} disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, { product_required: event.target.checked })} />
                  Prodotto obbligatorio
                </label>
                <label className="haccp-checkline">
                  <input type="checkbox" checked={rule.lot_required} disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, { lot_required: event.target.checked })} />
                  Lotto obbligatorio
                </label>
                <label className="haccp-checkline">
                  <input type="checkbox" checked={rule.photo_allowed} disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, {
                      photo_allowed: event.target.checked,
                      photo_required: event.target.checked ? rule.photo_required : false,
                      phases: event.target.checked ? rule.phases : rule.phases.map((phase) => ({
                        ...phase, photo_allowed: false, photo_required: false,
                      })),
                    })} />
                  Consenti foto delle misurazioni
                </label>
                <label className="haccp-checkline">
                  <input type="checkbox" checked={rule.photo_required} disabled={readOnly || !rule.photo_allowed}
                    onChange={(event) => updateRule(rule.equipment_id, {
                      photo_allowed: event.target.checked || rule.photo_allowed,
                      photo_required: event.target.checked,
                      phases: event.target.checked
                        ? rule.phases.map((phase) => ({ ...phase, photo_allowed: true }))
                        : rule.phases,
                    })} />
                  Foto obbligatoria per ogni fase
                </label>
                <label className="haccp-checkline">
                  <input type="checkbox" checked={rule.corrective_action_required} disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, { corrective_action_required: event.target.checked })} />
                  Azione correttiva obbligatoria in caso di anomalia
                </label>
                <label className="haccp-field haccp-field-wide">
                  <span>Procedura prevista in caso di non conformità</span>
                  <textarea rows="2" maxLength="2000" value={rule.nonconformity_procedure} disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, { nonconformity_procedure: event.target.value })} />
                </label>
                <label className="haccp-field haccp-field-wide">
                  <span>Istruzioni per l’azione correttiva</span>
                  <textarea rows="2" maxLength="2000" value={rule.corrective_action_instructions} disabled={readOnly}
                    onChange={(event) => updateRule(rule.equipment_id, { corrective_action_instructions: event.target.value })} />
                </label>
              </div>
              <div className="haccp-positive-phase-list">
                {rule.phases.map((phase, index) => (
                  <section className="haccp-positive-phase" key={`${rule.equipment_id}-${index}`}>
                    <div className="haccp-positive-phase-heading">
                      <h4>Fase {index + 1}</h4>
                      {!readOnly && rule.phases.length > 1 && (
                        <button type="button" className="haccp-icon-button" aria-label={`Rimuovi fase ${index + 1}`}
                          onClick={() => updateRule(rule.equipment_id, {
                            phases: rule.phases.filter((_, phaseIndex) => phaseIndex !== index),
                          })}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    <div className="haccp-positive-rule-grid">
                      <label className="haccp-field haccp-field-wide">
                        <span>Nome fase</span>
                        <input maxLength="100" value={phase.name} disabled={readOnly}
                          onChange={(event) => updatePhase(rule.equipment_id, index, { name: event.target.value })} />
                      </label>
                      <label className="haccp-field">
                        <span>Minimo °C</span>
                        <input type="number" step="0.1" value={phase.min_temperature_c} disabled={readOnly}
                          onChange={(event) => updatePhase(rule.equipment_id, index, { min_temperature_c: event.target.value })} />
                      </label>
                      <label className="haccp-field">
                        <span>Massimo °C</span>
                        <input type="number" step="0.1" value={phase.max_temperature_c} disabled={readOnly}
                          onChange={(event) => updatePhase(rule.equipment_id, index, { max_temperature_c: event.target.value })} />
                      </label>
                      <label className="haccp-field">
                        <span>Tolleranza °C <em>facoltativa</em></span>
                        <input type="number" min="0" step="0.1" value={phase.tolerance_c} disabled={readOnly}
                          onChange={(event) => updatePhase(rule.equipment_id, index, { tolerance_c: event.target.value })} />
                      </label>
                      <label className="haccp-field">
                        <span>Tempo massimo dalla partenza, minuti <em>facoltativo</em></span>
                        <input type="number" min="1" max="10080" step="1"
                          value={phase.deadline_minutes_after_start} disabled={readOnly}
                          onChange={(event) => updatePhase(rule.equipment_id, index, {
                            deadline_minutes_after_start: event.target.value,
                          })} />
                      </label>
                      <label className="haccp-checkline">
                        <input type="checkbox" checked={phase.required} disabled={readOnly}
                          onChange={(event) => updatePhase(rule.equipment_id, index, { required: event.target.checked })} />
                        Misurazione obbligatoria
                      </label>
                      <label className="haccp-checkline">
                        <input type="checkbox" checked={phase.photo_allowed} disabled={readOnly || !rule.photo_allowed || rule.photo_required}
                          onChange={(event) => updatePhase(rule.equipment_id, index, {
                            photo_allowed: event.target.checked,
                            photo_required: event.target.checked ? phase.photo_required : false,
                          })} />
                        Foto consentita
                      </label>
                      <label className="haccp-checkline">
                        <input type="checkbox" checked={phase.photo_required}
                          disabled={readOnly || !phase.photo_allowed || !rule.photo_allowed}
                          onChange={(event) => updatePhase(rule.equipment_id, index, { photo_required: event.target.checked })} />
                        Foto obbligatoria
                      </label>
                    </div>
                  </section>
                ))}
              </div>
              {!readOnly && (
                <button type="button" className="haccp-add-equipment"
                  onClick={() => updateRule(rule.equipment_id, { phases: [...rule.phases, emptyPhase()] })}>
                  <Plus size={16} /> Aggiungi fase
                </button>
              )}
            </article>
          ))}
          {error && <p role="alert" className="haccp-temperature-rule-error">{error}</p>}
          {message && <p role="status" className="haccp-temperature-success">{message}</p>}
          {configDirty && <p role="status" className="haccp-temperature-rule-note">Salva prima attività e attrezzature.</p>}
          {!readOnly && (
            <div className="haccp-temperature-rule-actions">
              <span className="haccp-readonly-note">{eligible.length} abbattitori configurabili</span>
              <button type="button" className="haccp-button haccp-button-secondary"
                disabled={!dirty || configDirty || saving || !rules.length || !rules.every(validRule)}
                onClick={save}>
                <Save size={15} /> {saving ? 'Salvataggio…' : 'Salva procedura'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
